import React, { useEffect, useRef, useState } from 'react';

/**
 * Looping landing clip. Autoplay unless the user prefers reduced motion,
 * in which case the first frame shows and a click plays.
 */
export default function LandingVideo({ src, width, height, ariaLabel, className }) {
  const videoRef = useRef(null);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (reduceMotion) {
      el.pause();
      const showFirstFrame = () => {
        try {
          el.currentTime = 0;
        } catch {
          /* ignore */
        }
      };
      if (el.readyState >= 1) showFirstFrame();
      else el.addEventListener('loadedmetadata', showFirstFrame, { once: true });
      return () => el.removeEventListener('loadedmetadata', showFirstFrame);
    }
    const play = el.play();
    if (play && typeof play.catch === 'function') play.catch(() => {});
  }, [reduceMotion, src]);

  const onClick = () => {
    if (!reduceMotion) return;
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      const play = el.play();
      if (play && typeof play.catch === 'function') play.catch(() => {});
    } else {
      el.pause();
    }
  };

  return (
    <video
      ref={videoRef}
      className={className}
      width={width}
      height={height}
      autoPlay={!reduceMotion}
      muted
      loop
      playsInline
      preload="metadata"
      controls={false}
      aria-label={ariaLabel}
      onClick={onClick}
      style={{
        width: '100%',
        height: 'auto',
        aspectRatio: `${width} / ${height}`,
        display: 'block',
        cursor: reduceMotion ? 'pointer' : undefined,
      }}
    >
      <source src={`${src}.webm`} type="video/webm" />
      <source src={`${src}.mp4`} type="video/mp4" />
    </video>
  );
}
